import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogIn, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { login, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const inputCls =
    "w-full bg-paper border-2 border-ink px-4 py-3 text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-pink transition-shadow duration-150";

export default function Login() {
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    if (user) {
        return <Navigate to="/host" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const data = await login(form.email, form.password);
            setUser(data);
            navigate("/host", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="login-page"
            className="min-h-[70vh] flex items-center justify-center px-5 py-20"
        >
            <Reveal>
                <form
                    onSubmit={submit}
                    data-testid="login-form"
                    className="w-full max-w-md bg-cream border-2 border-ink hard-shadow p-8 -rotate-1"
                >
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        Host only
                    </p>
                    <h1 className="mt-3 font-display font-black text-3xl text-ink tracking-tight">
                        Sign in to the inbox
                    </h1>
                    <p className="mt-2 text-sm text-ink/70">
                        Listener questions, stories and press inquiries live behind this door.
                    </p>

                    {error && (
                        <p
                            data-testid="login-error"
                            className="mt-5 border-2 border-magenta bg-magenta/10 text-magenta font-sub font-bold text-sm px-4 py-3"
                        >
                            {error}
                        </p>
                    )}

                    <label className="block mt-6">
                        <span className="font-mono-brand text-xs uppercase tracking-[0.2em] text-ink/60">
                            Email
                        </span>
                        <input
                            data-testid="login-email-input"
                            type="email"
                            required
                            className={`${inputCls} mt-2`}
                            placeholder="you@bomgay.in"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                    </label>
                    <label className="block mt-4">
                        <span className="font-mono-brand text-xs uppercase tracking-[0.2em] text-ink/60">
                            Password
                        </span>
                        <span className="relative block mt-2">
                            <input
                                data-testid="login-password-input"
                                type={showPassword ? "text" : "password"}
                                required
                                className={`${inputCls} pr-12`}
                                placeholder="••••••••"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                            />
                            <button
                                type="button"
                                data-testid="login-password-toggle"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                onClick={() => setShowPassword((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-ink/50 hover:text-pink transition-colors duration-150"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </span>
                    </label>

                    <button
                        type="submit"
                        data-testid="login-submit-btn"
                        disabled={busy}
                        className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-pink text-cream font-sub font-bold px-6 py-4 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                    >
                        <LogIn className="w-4 h-4" />
                        {busy ? "Signing in…" : "Sign in"}
                    </button>

                    <Link
                        to="/"
                        data-testid="login-back-link"
                        className="mt-5 inline-flex items-center gap-1.5 text-sm font-sub font-bold text-ink/60 hover:text-pink transition-colors duration-150"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to the site
                    </Link>
                </form>
            </Reveal>
        </div>
    );
}
